// Today's Workforce — the presence dashboard (Workforce Execution Phase 2,
// design doc 05 §3). Tabs: Today (counts + roster), History (presence days),
// Exceptions (resolve/ignore + manual attendance correction), Reports (hours
// by context). KaziPeople global theme tokens throughout.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { AlertTriangle, CalendarClock, ChevronLeft, ChevronRight, Loader2, RefreshCw, Users } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  ContextHoursRow, PresenceDay, PresenceException,
  contextHours, correctAttendanceDay, presenceExceptions, presenceHistory,
  presenceToday, resolvePresenceException,
} from '@/services/workAssignmentService';
import { type EmployeeMeta, facetOptions, useEmployeeNames } from './shared';
import { AttendanceDayDrawer, PresenceTable } from './PresenceTable';
import { AttendanceExceptionsPanel } from './AttendanceExceptionsPanel';
import { CorrectionDialog, type CorrectionTarget } from './CorrectionDialog';
import { useRbacRoles } from '@/rbac/useRbac';
import { ATTENDANCE_TEMPLATE_IDS, canSeeTemplate, getTemplate } from '@/features/reports/registry';
import { DAY_STATUS, SOURCE_CHIP, freshnessNote } from './presenceBasis';

const iso = (d: Date) => d.toISOString().slice(0, 10);
const daysAgo = (n: number) => iso(new Date(Date.now() - n * 86400000));

// Four buckets a manager asks about, mirroring presence.GroupOf server-side.
// `unverified` is deliberately NOT folded into "outside": an expected-remote
// employee with no scan, no check-in and no assignment has not been shown to
// be working anywhere, and a profile field is not evidence of presence.
const GROUP_ORDER = ['office', 'outside', 'away', 'unverified', 'unaccounted'];
const GROUP_LABEL: Record<string, string> = {
  office: 'In the office', outside: 'Outside the office', away: 'Away',
  unverified: 'Unverified', unaccounted: 'Unaccounted for',
};
// Each category's one-line definition — shown on the board, not only on hover.
// Verified attendance, field work and leave are never folded into each other.
const GROUP_HINT: Record<string, string> = {
  office: 'Attendance recorded at the office — a scanner punch or an office check-in',
  outside: 'Working on a checked-in or approved assignment away from the office — client site, field, route, remote, travel, training',
  away: 'Approved leave, a rest day or a public holiday',
  unverified: 'Not confirmed either way — expected remote with no evidence, or the scanner has not synced yet',
  unaccounted: 'A working day with no attendance, no approved leave and no assignment',
};

export const groupOfDay = (status: string, kind: string): string => {
  if (status === 'present') return kind === 'office' ? 'office' : 'outside';
  if (status === 'absent' || status === 'rest') return 'away';
  if (status === 'unverified') return 'unverified';
  if (status === 'missing') return 'unaccounted';
  return '';
};

const COUNT_ORDER = ['office', 'client_site', 'field', 'route', 'remote', 'travel', 'training', 'leave', 'unverified', 'rest', 'missing'];
const COUNT_LABEL: Record<string, string> = {
  office: 'Office', client_site: 'Client Site', field: 'Field', route: 'On Route',
  remote: 'Remote', travel: 'Travel', training: 'Training', leave: 'Leave',
  unverified: 'Remote (unverified)', rest: 'Rest', missing: 'Missing',
};

export const WorkforceOverviewPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const { nameOf, metaOf, meta, employees } = useEmployeeNames();
  const [tab, setTab] = useState<'today' | 'history' | 'exceptions' | 'reports'>('today');
  // The employee-day Attendance Detail — opened from Today and History.
  const [openDay, setOpenDay] = useState<PresenceDay | null>(null);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h2 text-charcoal flex items-center gap-2">
            <Users className="w-5 h-5" />
            {t('wa.overview_title', { defaultValue: "Today's Workforce" })}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('wa.overview_sub', { defaultValue: 'Who is working, where — composed from attendance, leave and work assignments.' })}
          </p>
        </div>
      </div>
      <div className="flex gap-1 border-b border-border">
        {(['today', 'history', 'exceptions', 'reports'] as const).map((k) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm rounded-t-md ${tab === k
              ? 'bg-card text-charcoal border border-border border-b-transparent font-medium'
              : 'text-muted-foreground hover:text-foreground'}`}>
            {t(`wa.tab_${k}`, { defaultValue: { today: 'Today', history: 'History', exceptions: 'Exceptions', reports: 'Reports' }[k] })}
          </button>
        ))}
      </div>
      {tab === 'today' && <TodayTab nameOf={nameOf} metaOf={metaOf} onOpen={setOpenDay} />}
      {tab === 'history' && (
        <HistoryTab nameOf={nameOf} metaOf={metaOf} meta={meta} employees={employees} onOpen={setOpenDay} />
      )}
      {tab === 'exceptions' && <ExceptionsTab nameOf={nameOf} onOpen={setOpenDay} />}
      {tab === 'reports' && <ReportsTab />}
      <AttendanceDayDrawer day={openDay} nameOf={nameOf} onClose={() => setOpenDay(null)} />
    </div>
  );
};

type RowProps = {
  nameOf: (id?: string | null) => string;
  metaOf: (id?: string | null) => EmployeeMeta;
  onOpen: (d: PresenceDay) => void;
};

const fmtClock = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
};

const TodayTab: React.FC<RowProps> = ({ nameOf, metaOf, onOpen }) => {
  const { t } = useTranslation('admin');
  const [data, setData] = useState<Awaited<ReturnType<typeof presenceToday>> | null>(null);
  const [filter, setFilter] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setBusy(true);
    presenceToday().then(setData)
      .catch((e) => toast.error(getApiErrorMessage(e, 'Could not load presence.')))
      .finally(() => setBusy(false));
  }, []);
  useEffect(load, [load]);

  const rows = useMemo(() => {
    const all = data?.employees || [];
    if (!filter) return all;
    if (GROUP_ORDER.includes(filter)) return all.filter((d) => groupOfDay(d.status, d.kind) === filter);
    return all.filter((d) => (filter === 'missing' ? d.status === 'missing'
      : filter === 'leave' ? d.status === 'absent'
      : filter === 'unverified' ? d.status === 'unverified'
      : filter === 'rest' ? d.status === 'rest'
      : d.kind === filter));
  }, [data, filter]);

  const countOf = (k: string) => {
    const c = data?.counts || {};
    if (k === 'rest') return c.rest || 0;
    return c[k] || 0;
  };
  const groupOf = (k: string) => data?.groups?.[k] || 0;
  const fresh = freshnessNote(data?.freshness, fmtClock);

  return (
    <div className="space-y-4">
      {/* Showing a day that is not today is the normal case for a tenant whose
          scanner is pulled weekly. Saying which day, and why, is the whole
          difference between "last Friday" and a dashboard quietly lying. */}
      {data && !data.is_today && (
        <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning-soft p-3 text-sm">
          <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
          <span className="text-foreground">
            {t('wa.as_of', {
              defaultValue:
                'Nothing has reached us for {{today}} yet, so this shows {{shown}} — the last day we hold. Attendance that arrives as a weekly upload will always lag like this.',
              today: data.requested_date, shown: data.date,
            })}
          </span>
        </div>
      )}
      {fresh.tone !== 'none' && (
        <div className={`flex items-start gap-2 rounded-md border p-3 text-sm ${fresh.tone === 'warning'
          ? 'border-warning bg-warning-soft' : 'border-border bg-card'}`}>
          <CalendarClock className={`mt-0.5 h-4 w-4 shrink-0 ${fresh.tone === 'warning' ? 'text-warning' : 'text-muted-foreground'}`} aria-hidden />
          <span className="text-foreground">{t(fresh.key, { defaultValue: fresh.defaultValue, ...(fresh.values || {}) })}</span>
        </div>
      )}
      {(data?.freshness?.backupReminders || []).map((r, i) => (
        <p key={`backup-${i}`} className="text-sm text-warning">
          <AlertTriangle className="mr-1 inline h-4 w-4" aria-hidden />
          {t('wa.backup_reminder', {
            defaultValue: 'Backup check-in has been on for {{n}} days at {{where}}. Turn it off on the Attendance policy screen once the scanner works again.',
            n: r.days,
            where: r.scope === 'default' || !r.branchName
              ? t('wa.backup_all_offices', { defaultValue: 'all offices (company default)' })
              : r.branchName,
          })}
        </p>
      ))}
      {(data?.freshness?.unmatchedScannerCodes || 0) > 0 && (
        <p className="text-sm text-warning">
          <AlertTriangle className="mr-1 inline h-4 w-4" aria-hidden />
          {t('wa.unmatched_codes', {
            defaultValue: '{{n}} scanner codes are not matched to anyone — their punches cannot count for anybody yet.',
            n: data?.freshness?.unmatchedScannerCodes,
          })}{' '}
          <Link to="../attendance" className="underline">{t('wa.unmatched_fix', { defaultValue: 'Match them' })}</Link>
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {GROUP_ORDER.filter((k) => groupOf(k) > 0 || ['office', 'outside', 'unaccounted'].includes(k)).map((k) => (
          <button key={k} onClick={() => setFilter(filter === k ? null : k)}
            title={GROUP_HINT[k]}
            className={`rounded-lg border px-3 py-2 text-left min-w-[130px] transition
              ${filter === k ? 'border-primary-500 bg-primary-500/5' : 'border-border bg-card hover:border-primary-500/40'}`}>
            <p className="text-2xl font-semibold text-charcoal tabular-nums">{groupOf(k)}</p>
            <p className="text-xs text-muted-foreground">{GROUP_LABEL[k]}</p>
          </button>
        ))}
        <span className="mx-1 hidden w-px self-stretch bg-border sm:block" />
        {/* The precise kind is still one click away — "outside" is a summary,
            not a replacement for knowing someone is on a client site. */}
        {COUNT_ORDER.filter((k) => countOf(k) > 0).map((k) => (
          <button key={k} onClick={() => setFilter(filter === k ? null : k)}
            className={`rounded-lg border px-2.5 py-2 text-left transition
              ${filter === k ? 'border-primary-500 bg-primary-500/5' : 'border-border bg-card hover:border-primary-500/40'}`}>
            <p className="text-base font-semibold text-charcoal tabular-nums">{countOf(k)}</p>
            <p className="text-[11px] text-muted-foreground">{COUNT_LABEL[k]}</p>
          </button>
        ))}
        <Button variant="ghost" size="sm" className="self-start" onClick={load} disabled={busy}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>
      {/* What each bucket means, on the board — not only on hover. */}
      <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2 lg:grid-cols-3">
        {GROUP_ORDER.map((k) => (
          <div key={k} className="flex gap-1">
            <dt className="font-medium text-foreground">{t(`wa.group_${k}`, { defaultValue: GROUP_LABEL[k] })}:</dt>
            <dd className="text-muted-foreground">{t(`wa.group_hint_${k}`, { defaultValue: GROUP_HINT[k] })}</dd>
          </div>
        ))}
      </dl>
      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">
              {data && (data.employees || []).length === 0
                ? t('wa.no_signal', { defaultValue: 'No workforce signal on record yet — presence appears as scans, assignments and leave arrive. If attendance is uploaded weekly, expect this until the next upload.' })
                : t('wa.no_match', { defaultValue: 'Nobody in this bucket right now.' })}
            </p>
          ) : (
            <PresenceTable rows={rows} nameOf={nameOf} metaOf={metaOf} onOpen={onOpen} />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const PAGE = 50;
const selectCls = 'h-9 rounded-md border border-border bg-card px-2 text-sm text-foreground';

const HistoryTab: React.FC<RowProps & {
  meta: Record<string, EmployeeMeta>;
  employees: Array<{ id: string; name: string }>;
}> = ({ nameOf, metaOf, onOpen, meta, employees }) => {
  const { t } = useTranslation('admin');
  const [from, setFrom] = useState(daysAgo(7));
  const [to, setTo] = useState(daysAgo(0));
  const [employeeId, setEmployeeId] = useState('');
  const [department, setDepartment] = useState('');
  const [branch, setBranch] = useState('');
  const [source, setSource] = useState('');
  const [status, setStatus] = useState('');
  const [exceptionsOnly, setExceptionsOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<{ days: PresenceDay[]; total: number }>({ days: [], total: 0 });
  const [busy, setBusy] = useState(false);
  const departments = useMemo(() => facetOptions(meta, 'department'), [meta]);
  const branches = useMemo(() => facetOptions(meta, 'branch'), [meta]);

  // Every filter is applied SERVER-side, on the caller's own scope.
  const load = useCallback(() => {
    setBusy(true);
    presenceHistory({
      from, to, employeeId: employeeId || undefined, department: department || undefined,
      branch: branch || undefined, source: source || undefined, status: status || undefined,
      exceptionsOnly, limit: PAGE, offset,
    }).then((p) => setPage({ days: p.days, total: p.total }))
      .catch((e) => toast.error(getApiErrorMessage(e, 'Could not load presence history.')))
      .finally(() => setBusy(false));
  }, [from, to, employeeId, department, branch, source, status, exceptionsOnly, offset]);
  useEffect(load, [load]);
  // Any filter change starts again at page 1.
  useEffect(() => { setOffset(0); }, [from, to, employeeId, department, branch, source, status, exceptionsOnly]);

  const last = Math.min(offset + PAGE, page.total);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div><Label>{t('wa.from', { defaultValue: 'From' })}</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div><Label>{t('wa.to', { defaultValue: 'To' })}</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <div>
          <Label>{t('wa.col_employee', { defaultValue: 'Employee' })}</Label>
          <select className={selectCls} value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="">{t('wa.all', { defaultValue: 'All' })}</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        {departments.length > 0 && (
          <div>
            <Label>{t('wa.department', { defaultValue: 'Department' })}</Label>
            <select className={selectCls} value={department} onChange={(e) => setDepartment(e.target.value)}>
              <option value="">{t('wa.all', { defaultValue: 'All' })}</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        )}
        {branches.length > 0 && (
          <div>
            <Label>{t('wa.branch', { defaultValue: 'Branch' })}</Label>
            <select className={selectCls} value={branch} onChange={(e) => setBranch(e.target.value)}>
              <option value="">{t('wa.all', { defaultValue: 'All' })}</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <Label>{t('wa.col_source', { defaultValue: 'Source' })}</Label>
          <select className={selectCls} value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">{t('wa.all', { defaultValue: 'All' })}</option>
            {Object.entries(SOURCE_CHIP).map(([k, v]) => (
              <option key={k} value={k}>{t(`wa.chip_${k}`, { defaultValue: v.label })}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>{t('wa.col_status', { defaultValue: 'Status' })}</Label>
          <select className={selectCls} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">{t('wa.all', { defaultValue: 'All' })}</option>
            {Object.entries(DAY_STATUS).map(([k, v]) => (
              <option key={k} value={k}>{t(`wa.status_${k}`, { defaultValue: v.label })}</option>
            ))}
          </select>
        </div>
        <label className="flex h-9 items-center gap-2 text-sm text-foreground">
          <input type="checkbox" checked={exceptionsOnly} onChange={(e) => setExceptionsOnly(e.target.checked)} />
          {t('wa.exceptions_only', { defaultValue: 'Exceptions only' })}
        </label>
        <Button variant="outline" onClick={load} disabled={busy}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.refresh', { defaultValue: 'Refresh' })}
        </Button>
      </div>
      <Card><CardContent className="p-0">
        {page.days.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">
            {t('wa.no_days', { defaultValue: 'No resolved presence days match (materialized nightly and whenever attendance changes).' })}
          </p>
        ) : (
          <PresenceTable rows={page.days} nameOf={nameOf} metaOf={metaOf} onOpen={onOpen} showDate />
        )}
      </CardContent></Card>
      {page.total > 0 && (
        <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
          <span>{t('wa.page_of', { defaultValue: '{{first}}–{{last}} of {{total}}', first: offset + 1, last, total: page.total })}</span>
          <Button variant="outline" size="sm" disabled={offset === 0 || busy} onClick={() => setOffset(Math.max(0, offset - PAGE))}
            aria-label={t('wa.prev', { defaultValue: 'Previous page' })}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" disabled={last >= page.total || busy} onClick={() => setOffset(offset + PAGE)}
            aria-label={t('wa.next', { defaultValue: 'Next page' })}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};


const ExceptionsTab: React.FC<{
  nameOf: (id?: string | null) => string;
  onOpen: (d: PresenceDay) => void;
}> = ({ nameOf, onOpen }) => {
  const { t } = useTranslation('admin');
  const [list, setList] = useState<PresenceException[]>([]);
  const [correcting, setCorrecting] = useState<CorrectionTarget | null>(null);
  const load = useCallback(() => {
    presenceExceptions('open').then(setList)
      .catch((e) => toast.error(getApiErrorMessage(e, 'Could not load exceptions.')));
  }, []);
  useEffect(load, [load]);

  const decide = async (id: string, status: 'resolved' | 'ignored') => {
    try {
      await resolvePresenceException(id, status);
      toast.success(t('wa.exception_updated', { defaultValue: 'Exception updated.' }));
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not update the exception.'));
    }
  };

  const sev: Record<string, string> = {
    info: 'bg-info-soft text-info-strong',
    warning: 'bg-warning-soft text-warning-strong',
    critical: 'bg-danger-soft text-danger-strong',
  };
  return (
    <div className="space-y-4">
    {/* P3: the attendance review queue — severity, filters, evidence, reasons. */}
    <AttendanceExceptionsPanel nameOf={nameOf} onOpen={onOpen} onCorrect={setCorrecting} />
    <Card><CardContent className="p-0">
      <h3 className="px-4 pt-4 text-sm font-semibold text-charcoal">{t('wa.exc_presence_title', { defaultValue: 'Presence exceptions' })}</h3>
      {list.length === 0 ? (
        <p className="p-6 text-sm text-muted-foreground">{t('wa.no_presence_exceptions', { defaultValue: 'No open presence exceptions.' })}</p>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground border-b border-border">
            <th className="py-2 px-4">{t('wa.col_date', { defaultValue: 'Date' })}</th>
            <th className="py-2 px-4">{t('wa.col_employee', { defaultValue: 'Employee' })}</th>
            <th className="py-2 px-4">{t('wa.col_type', { defaultValue: 'Type' })}</th>
            <th className="py-2 px-4" />
          </tr></thead>
          <tbody>{list.map((ex) => (
            <tr key={ex.id} className="border-b border-border">
              <td className="py-2 px-4 tabular-nums text-foreground">{ex.workDate}</td>
              <td className="py-2 px-4 text-foreground">{nameOf(ex.employeeId)}</td>
              <td className="py-2 px-4">
                <Badge variant="secondary" className={sev[ex.severity]}>
                  <AlertTriangle className="w-3 h-3 mr-1" />{ex.type.replace(/_/g, ' ')}
                </Badge>
              </td>
              <td className="py-2 px-4 text-right space-x-2 whitespace-nowrap">
                <Button size="sm" variant="ghost" onClick={() => onOpen({
                  employeeId: ex.employeeId, date: ex.workDate, status: 'present', kind: '', kindLabel: '', evidence: {},
                })}>
                  {t('wa.exc_evidence', { defaultValue: 'Evidence' })}
                </Button>
                {(ex.type === 'missing_status' || ex.type === 'missing_clock_out') && (
                  <Button size="sm" variant="outline" onClick={() => setCorrecting({ employeeId: ex.employeeId, workDate: ex.workDate })}>
                    {t('wa.correct_attendance', { defaultValue: 'Correct attendance' })}
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => decide(ex.id, 'resolved')}>
                  {t('wa.resolve', { defaultValue: 'Resolve' })}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => decide(ex.id, 'ignored')}>
                  {t('wa.ignore', { defaultValue: 'Ignore' })}
                </Button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </CardContent></Card>
      {correcting && (
        <CorrectionDialog ex={correcting} nameOf={nameOf}
          onClose={() => setCorrecting(null)}
          onDone={() => { setCorrecting(null); load(); }} />
      )}
    </div>
  );
};

const ReportsTab: React.FC = () => {
  const { t } = useTranslation('admin');
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(daysAgo(0));
  const [rows, setRows] = useState<ContextHoursRow[]>([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setBusy(true);
    contextHours(from, to).then(setRows)
      .catch((e) => toast.error(getApiErrorMessage(e, 'Could not load the report.')))
      .finally(() => setBusy(false));
  }, [from, to]);
  useEffect(load, [load]);
  return (
    <div className="space-y-3">
      <div className="flex items-end gap-2">
        <div><Label>{t('wa.from', { defaultValue: 'From' })}</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div><Label>{t('wa.to', { defaultValue: 'To' })}</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <Button variant="outline" onClick={load} disabled={busy}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.refresh', { defaultValue: 'Refresh' })}
        </Button>
      </div>
      <Card><CardContent className="p-0">
        {rows.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">
            {t('wa.no_report', { defaultValue: 'No approved, completed assignment hours in this range yet.' })}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b border-border">
              <th className="py-2 px-4">{t('wa.col_context', { defaultValue: 'Context' })}</th>
              <th className="py-2 px-4">{t('wa.col_type', { defaultValue: 'Type' })}</th>
              <th className="py-2 px-4 text-right">{t('wa.col_hours', { defaultValue: 'Hours' })}</th>
              <th className="py-2 px-4 text-right">{t('wa.col_assignments', { defaultValue: 'Assignments' })}</th>
              <th className="py-2 px-4 text-right">{t('wa.col_people', { defaultValue: 'People' })}</th>
            </tr></thead>
            <tbody>{rows.map((r, i) => (
              <tr key={i} className="border-b border-border">
                <td className="py-2 px-4">
                  <span className="text-foreground">{r.contextLabel}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{r.contextType.replace('_', ' ')}</span>
                </td>
                <td className="py-2 px-4 text-muted-foreground">{r.typeKey.replace(/_/g, ' ')}</td>
                <td className="py-2 px-4 text-right tabular-nums text-foreground">{r.hours.toFixed(1)}</td>
                <td className="py-2 px-4 text-right tabular-nums text-foreground">{r.assignments}</td>
                <td className="py-2 px-4 text-right tabular-nums text-foreground">{r.employees}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </CardContent></Card>
      <p className="text-xs text-muted-foreground">
        {t('wa.report_note', { defaultValue: 'Approved, completed assignment hours by work context. Payroll impact is computed separately by payroll policy.' })}
      </p>
      <AttendanceReportLinks />
    </div>
  );
};

// P4: the standard attendance reports, launched into the EXISTING report
// runner (preview, CSV, server XLSX/CSV export) — not a second report UI.
// Only the templates this viewer may run are listed; the runner and the
// engine enforce it again server-side.
const AttendanceReportLinks: React.FC = () => {
  const { t } = useTranslation('admin');
  const roles = useRbacRoles();
  const templates = ATTENDANCE_TEMPLATE_IDS
    .map((id) => getTemplate(id))
    .filter((tpl): tpl is NonNullable<typeof tpl> => !!tpl && canSeeTemplate(roles, tpl));
  if (templates.length === 0) return null;
  return (
    <Card><CardContent className="p-4 space-y-2">
      <h3 className="text-sm font-semibold text-charcoal">{t('wa.attendance_reports', { defaultValue: 'Attendance reports' })}</h3>
      <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {templates.map((tpl) => (
          <li key={tpl.id} className="rounded-md border border-border p-3">
            <Link to={`../reports/${tpl.id}`} className="text-sm font-medium text-foreground hover:underline">
              {t(`wa.rpt_${tpl.id}`, { defaultValue: tpl.name })}
            </Link>
            <p className="text-xs text-muted-foreground">{t(`wa.rpt_${tpl.id}_desc`, { defaultValue: tpl.description })}</p>
          </li>
        ))}
      </ul>
    </CardContent></Card>
  );
};

export default WorkforceOverviewPage;
